import { memo } from "react";
import styled from "styled-components";

import { AnnotationIcon, CameraIcon } from "../../icons/glyphs";
import { IconButton } from "../IconButton";
import { formatMediaPlaybackTime } from "../MediaTimelineBar";
import { Spinner } from "../Spinner";
import { TruncatedText } from "../TruncatedText";
import { AnnotationSetValidationAction } from "./AnnotationSetValidationAction";
import type { AnnotationValidationIssueLocation } from "./AnnotationSetValidationAction";
import { AnnotationStudioHelpAction } from "./AnnotationStudioHelpAction";
import { annotationStudioText } from "./messages";
import type {
  AnnotationFrameDraft,
  AnnotationSetValidationIssue,
  AnnotationSetValidationState,
  AnnotationStudioLocale,
  AnnotationStudioViewMode,
  AnnotationTarget,
} from "./types";
import { STUDIO_BREAKPOINT, STUDIO_COMMAND_BAR_HEIGHT } from "./MediaAnnotationStudioConstraint";

export type FrameEditingStatusState = "idle" | "dirty" | "saving" | "viewing";
export type StudioExitActionState = "idle" | "saving" | "checking" | "leaving";

export interface StudioCommandBarProps {
  readonly locale: AnnotationStudioLocale;
  readonly viewMode: AnnotationStudioViewMode;
  readonly frame?: AnnotationFrameDraft;
  readonly targets: readonly AnnotationTarget[];
  readonly viewportContextLabel: string;
  readonly frameEditingStatusState: FrameEditingStatusState;
  readonly validationState: AnnotationSetValidationState | null;
  readonly validationSummaryLocation: string | null;
  readonly validationIssueLocations: ReadonlyMap<string, AnnotationValidationIssueLocation>;
  readonly validationReviewDestination: Readonly<{
    frameId: string;
    targetId?: string | null;
  }> | null;
  readonly validationPopupOpenRequestId: number;
  readonly materializingFrameId?: string | null;
  readonly hasUnsavedAnnotationChanges: boolean;
  readonly hasAnnotationContent: boolean;
  readonly exitActionState: StudioExitActionState;
  readonly exitAvailable: boolean;
  readonly exitDisabled: boolean;
  readonly onValidate: () => void;
  readonly onOpenValidationIssue: (
    issue: AnnotationSetValidationIssue,
    frameId: string,
  ) => boolean | Promise<boolean>;
  readonly onOpenValidationReview: (frameId: string, targetId?: string | null) => void;
  readonly onExit: () => void;
}

const Root = styled.header`
  box-sizing: border-box;
  display: grid;
  height: ${STUDIO_COMMAND_BAR_HEIGHT};
  min-width: 0;
  flex: 0 0 auto;
  grid-template-areas: "context actions";
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: 0.45rem 0.75rem;
  padding: 0.45rem 0.65rem;
  border-bottom: 1px solid ${({ theme }) => theme.color.border.divider};
  background: ${({ theme }) => theme.color.bg.surfaceSunken};

  @container media-annotation-studio (max-width: ${STUDIO_BREAKPOINT.tightCommandBar}) {
    min-height: ${STUDIO_COMMAND_BAR_HEIGHT};
  }
`;

const Context = styled.div`
  grid-area: context;
  display: flex;
  min-width: 0;
  flex: 1 1 auto;
  align-items: center;
  overflow: hidden;
`;

const Actions = styled.div`
  grid-area: actions;
  display: inline-flex;
  min-width: 0;
  flex: 0 0 auto;
  align-items: center;
  justify-self: end;
  gap: 0.35rem;
  white-space: nowrap;
`;

const FrameHeaderMeta = styled.span`
  display: grid;
  width: min(48rem, 100%);
  max-width: 100%;
  min-width: 0;
  flex: 1 1 auto;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: 0.45rem;
  color: ${({ theme }) => theme.color.text.default};
  font-size: ${({ theme }) => theme.typography.size.body};
  font-variant-numeric: tabular-nums;
  font-weight: ${({ theme }) => theme.typography.weight.medium};
`;

const FrameIdentity = styled.span`
  display: grid;
  min-width: 0;
  align-items: start;
  gap: 0.1rem;
`;

const ContextKind = styled.span`
  display: inline-flex;
  min-width: 0;
  align-items: center;
  gap: 0.3rem;
  color: ${({ theme }) => theme.color.text.default};
  font-size: ${({ theme }) => theme.typography.size.caption};
  font-weight: ${({ theme }) => theme.typography.weight.bold};
  line-height: ${({ theme }) => theme.typography.lineHeight.tight};

  svg {
    flex: 0 0 auto;
    font-size: ${({ theme }) => theme.typography.size.body};
  }
`;

const Timestamp = styled.span`
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  white-space: nowrap;
`;

const FrameContext = styled(TruncatedText)`
  color: ${({ theme }) => theme.color.text.subtle};
  font-size: ${({ theme }) => theme.typography.size.caption};
  font-weight: ${({ theme }) => theme.typography.weight.regular};
`;

const BrowseContext = styled(TruncatedText)`
  max-width: min(48rem, 100%);
  color: ${({ theme }) => theme.color.text.subtle};
  font-size: ${({ theme }) => theme.typography.size.caption};
  font-weight: ${({ theme }) => theme.typography.weight.regular};
  text-align: left;
`;

const BrowseIdentity = styled.span`
  display: grid;
  width: min(48rem, 100%);
  min-width: 0;
  gap: 0.1rem;
`;

const EditingStatus = styled.span<{ $state: FrameEditingStatusState }>`
  display: inline-flex;
  min-height: 1.35rem;
  align-items: center;
  gap: 0.35rem;
  padding: 0;
  border: 0;
  background: transparent;
  color: ${({ $state, theme }) =>
    $state === "dirty" || $state === "saving" ? theme.color.text.default : theme.color.text.subtle};
  font-size: ${({ theme }) => theme.typography.size.caption};
  font-weight: ${({ theme }) => theme.typography.weight.medium};
  line-height: 1;
  white-space: nowrap;
`;

const EditingStatusDot = styled.span`
  width: 0.4rem;
  height: 0.4rem;
  flex: 0 0 auto;
  border-radius: 50%;
  background: ${({ theme }) => theme.color.border.selected};
`;

function StudioCommandBarComponent({
  locale,
  viewMode,
  frame,
  targets,
  viewportContextLabel,
  frameEditingStatusState,
  validationState,
  validationSummaryLocation,
  validationIssueLocations,
  validationReviewDestination,
  validationPopupOpenRequestId,
  materializingFrameId = null,
  hasUnsavedAnnotationChanges,
  hasAnnotationContent,
  exitActionState,
  exitAvailable,
  exitDisabled,
  onValidate,
  onOpenValidationIssue,
  onOpenValidationReview,
  onExit,
}: StudioCommandBarProps) {
  return (
    <Root data-studio-command-bar="">
      <Context data-studio-command-bar-context="">
        {viewMode === "annotate" && frame ? (
          <FrameHeaderMeta>
            <FrameIdentity data-frame-header-identity="">
              <ContextKind data-studio-context-kind="annotation">
                <AnnotationIcon aria-hidden="true" />
                <span>{annotationStudioText("annotationContext", locale)}</span>
                <Timestamp data-frame-header-timestamp="">
                  · {formatMediaPlaybackTime(frame.timestampSeconds)}
                </Timestamp>
              </ContextKind>
              <FrameContext data-frame-context="editing" fullText={viewportContextLabel}>
                {viewportContextLabel}
              </FrameContext>
            </FrameIdentity>
            <EditingStatus
              $state={frameEditingStatusState}
              data-frame-editing-status=""
              data-state={frameEditingStatusState}
              role="status"
              aria-atomic="true"
            >
              {frameEditingStatusState === "saving" ? (
                <Spinner size="sm" />
              ) : frameEditingStatusState === "dirty" ? (
                <EditingStatusDot data-frame-editing-status-dot="" />
              ) : null}
              {annotationStudioText(
                frameEditingStatusState === "saving"
                  ? "savingStatus"
                  : frameEditingStatusState === "dirty"
                    ? "unsaved"
                    : frameEditingStatusState === "viewing"
                      ? "viewingAnnotation"
                      : "addMarkerHint",
                locale,
              )}
            </EditingStatus>
          </FrameHeaderMeta>
        ) : (
          <BrowseIdentity>
            <ContextKind data-studio-context-kind="video">
              <CameraIcon aria-hidden="true" />
              <span>{annotationStudioText("videoContext", locale)}</span>
            </ContextKind>
            <BrowseContext
              data-frame-context="browse"
              fullText={viewportContextLabel}
              aria-label={`${annotationStudioText(
                "currentVideoContext",
                locale,
              )}: ${viewportContextLabel}`}
            >
              {viewportContextLabel}
            </BrowseContext>
          </BrowseIdentity>
        )}
      </Context>
      <Actions data-studio-command-bar-actions="">
        <AnnotationStudioHelpAction locale={locale} />
        {validationState ? (
          <AnnotationSetValidationAction
            state={validationState}
            locale={locale}
            targets={targets}
            onValidate={onValidate}
            onOpenIssue={onOpenValidationIssue}
            onOpenSummary={
              validationReviewDestination
                ? () =>
                    onOpenValidationReview(
                      validationReviewDestination.frameId,
                      validationReviewDestination.targetId,
                    )
                : null
            }
            summaryLocation={validationSummaryLocation}
            issueLocations={validationIssueLocations}
            currentFrameId={frame?.frameId ?? null}
            materializingFrameId={materializingFrameId}
            openRequestId={validationPopupOpenRequestId}
            hasUnsavedAnnotationChanges={hasUnsavedAnnotationChanges}
            hasAnnotationContent={hasAnnotationContent}
          />
        ) : null}
        {exitAvailable ? (
          <IconButton
            data-studio-exit-action=""
            size="sm"
            variant="surface"
            text={annotationStudioText(
              exitActionState === "saving"
                ? "savingAndExit"
                : exitActionState === "checking"
                  ? "checkingAnnotations"
                  : exitActionState === "leaving"
                    ? "exitingStudio"
                    : hasUnsavedAnnotationChanges
                      ? "saveAndExit"
                      : "exitStudio",
              locale,
            )}
            disabled={exitDisabled}
            onClick={onExit}
          >
            {exitActionState !== "idle" ? (
              <Spinner
                label={annotationStudioText(
                  exitActionState === "saving"
                    ? "savingAndExit"
                    : exitActionState === "checking"
                      ? "checkingAnnotations"
                      : "exitingStudio",
                  locale,
                )}
                size="sm"
              />
            ) : null}
          </IconButton>
        ) : null}
      </Actions>
    </Root>
  );
}

export const StudioCommandBar = memo(StudioCommandBarComponent);
